// @file The store for handling the surface onboarding demo padlet panel and its related logic
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { WallCreatedFromType } from '@@/enums'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export enum DemoPadletPanelStep {
  Landing = 'landing',
  Topic = 'topic',
  Post = 'post',
}

export enum StepStatus {
  NotStarted = 'notstarted',
  InProgress = 'inprogress',
  Complete = 'complete',
}

export const LANDING_PANEL_STEPS = [
  { text: __('Set a topic') },
  { text: __('Add some posts') },
  { text: __('Share and collaborate') },
]

interface HeaderContent {
  title: string
  number: number
}

export const useSurfaceOnboardingDemoPadletPanelStore = defineStore('surfaceOnboardingDemoPadletPanelStore', () => {
  const windowSizeStore = useWindowSizeStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()

  // ------------------------------
  // General
  // ------------------------------

  const isDemoPadletPanel = computed(() => {
    return (
      isAppUsing('demoPadletPanel') &&
      surfaceStartingStateStore.startingState.onboardingFlow?.wallCreatedFromType === WallCreatedFromType.demo
    )
  })
  const isDemoPadletPanelMobile = computed(() => {
    return isDemoPadletPanel.value && !windowSizeStore.isBiggerThanTabletPortrait
  })
  const isDemoPadletPanelDesktop = computed(() => {
    // Remember this should evaluate to false when demo padlet onboarding is completed
    return isDemoPadletPanel.value && windowSizeStore.isBiggerThanTabletPortrait
  })

  // The activeStep controls which step panel that is displayed, the activeStepStatus controls the progress of the active step
  const activeStep = ref(DemoPadletPanelStep.Landing)
  const activeStepStatus = ref(StepStatus.NotStarted)
  function setActiveStepStatus(status: StepStatus): void {
    activeStepStatus.value = status
  }

  const isStepNotStarted = computed(() => {
    return activeStepStatus.value === StepStatus.NotStarted
  })
  const isStepInProgress = computed(() => {
    return activeStepStatus.value === StepStatus.InProgress
  })
  const isStepCompleted = computed(() => {
    return activeStepStatus.value === StepStatus.Complete
  })

  function goToNextStep(): void {
    switch (activeStep.value) {
      case DemoPadletPanelStep.Landing:
        activeStep.value = DemoPadletPanelStep.Topic
        break
      case DemoPadletPanelStep.Topic:
        activeStep.value = DemoPadletPanelStep.Post
        break
      default:
        activeStep.value = DemoPadletPanelStep.Landing
    }
    // Every time we change steps we reset progress
    setActiveStepStatus(StepStatus.NotStarted)
  }

  function getHeaderContent(step): HeaderContent {
    switch (step) {
      case DemoPadletPanelStep.Topic:
        return {
          title: __('Set a topic'),
          number: 1,
        }
      case DemoPadletPanelStep.Post:
        return {
          title: __('Post'),
          number: 2,
        }
      default:
        return {
          title: '',
          number: 0,
        }
    }
  }
  const currentHeader = computed(() => getHeaderContent(activeStep.value))

  // ------------------------------
  // Hiding UI
  // ------------------------------

  // Master getter to prevent long conditional rendering logic in markup
  const isDemoPadletPanelAndShouldHideUI = computed(() => {
    return isDemoPadletPanel.value && shouldHideTopicStepElements.value
  })

  const shouldHideTopicStepElements = computed(() => {
    // TODO: ENG-15161 refactor when upserter is implemented and check step correctly
    return isDemoPadletPanelDesktop.value && activeStep.value === DemoPadletPanelStep.Topic && !hasSetTopic.value
    // Note: use || to add getters for other steps
  })

  // ------------------------------
  // Landing step
  // ------------------------------

  const isLandingStep = computed(() => {
    return (
      // TODO: ENG-15161 Refactor to remove this temporary check against activeStep and only read values from surfaceStartingStateStore (this will be after the upserter is implemented)
      // using this line: surfaceStartingStateStore.startingState.onboardingFlow?.wallCreatedFromType === WallCreatedFromType.demo
      activeStep.value === DemoPadletPanelStep.Landing
    )
  })

  // ------------------------------
  // Topic step
  // ------------------------------

  const hasSetTopic = ref(false)
  function setHasSetTopic(value: boolean): void {
    hasSetTopic.value = value
  }

  return {
    // ------------------------------
    // General
    // ------------------------------
    isDemoPadletPanel,
    isDemoPadletPanelMobile,
    isDemoPadletPanelDesktop,
    activeStep,
    activeStepStatus,
    setActiveStepStatus,
    isStepNotStarted,
    isStepInProgress,
    isStepCompleted,
    goToNextStep,
    getHeaderContent,
    currentHeader,
    // ------------------------------
    // Hiding UI
    // ------------------------------
    isDemoPadletPanelAndShouldHideUI,
    shouldHideTopicStepElements,
    // ------------------------------
    // Landing step
    // ------------------------------
    isLandingStep,
    // ------------------------------
    // Topic step
    // ------------------------------
    hasSetTopic,
    setHasSetTopic,
  }
})
